import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle, Wind, Activity, ChevronRight, ArrowRight, CheckCircle, Layers, Waves, Heart, Youtube, Brain, Sparkles, Target, ClipboardList } from 'lucide-react';
import SiteHeader from '@/components/website/SiteHeader';
import SiteFooter from '@/components/website/SiteFooter';
import ReviewsSection from '@/components/website/ReviewsSection';

const HomePage = () => {
  const navigate = useNavigate();

  const conditions = [
    {
      icon: AlertCircle,
      title: "Hyperventilation",
      description: "Découvrez comment reconnaître et traiter le syndrome d'hyperventilation chronique.",
      link: "/hyperventilation",
      color: "from-rose-500 to-orange-500"
    },
    {
      icon: Wind,
      title: "Respiration Dysfonctionnelle",
      description: "Comprenez les patterns respiratoires inadaptés et comment les corriger.",
      link: "/respiration-dysfonctionnelle",
      color: "from-sky-500 to-sky-600"
    },
    {
      icon: Waves,
      title: "Asthme",
      description: "Techniques respiratoires pour gérer et réduire les symptômes de l'asthme.",
      link: "/asthme",
      color: "from-indigo-500 to-blue-500"
    },
    {
      icon: Activity,
      title: "COVID Long",
      description: "Rééducation respiratoire pour les symptômes persistants post-COVID.",
      link: "/covid-long",
      color: "from-violet-500 to-purple-500"
    }
  ];

  const educationalResources = [
    {
      icon: Brain,
      title: "Causes des Troubles Respiratoires",
      description: "Le rôle du système nerveux autonome dans l'apparition des dysfonctionnements respiratoires.",
      link: "/causes-troubles-respiratoires",
      color: "from-sky-500 to-sky-600"
    },
    {
      icon: Layers,
      title: "Mécanique Respiratoire",
      description: "Anatomie et physiologie : comprendre comment fonctionne la respiration.",
      link: "/mecanique-respiratoire",
      color: "from-teal-500 to-emerald-500"
    },
    {
      icon: Sparkles,
      title: "Respiration Optimale",
      description: "Découvrez les 6 principes fondamentaux pour une respiration parfaite et ses bénéfices.",
      link: "/respiration-optimale",
      color: "from-sky-500 to-sky-600"
    },
    {
      icon: Target,
      title: "Méthode Buteyko",
      description: "Technique de respiration pour améliorer la tolérance au CO₂ et réduire l'hyperventilation.",
      link: "/methode-buteyko",
      color: "from-sky-500 to-indigo-500"
    },
    {
      icon: Heart,
      title: "Cohérence Cardiaque",
      description: "Technique scientifique pour harmoniser le cœur, le cerveau et les émotions.",
      link: "/coherence-cardiaque",
      color: "from-sky-500 to-sky-600"
    },
    {
      icon: Activity,
      title: "Ma Chaîne YouTube",
      description: "Vidéos d'exercices respiratoires et techniques de rééducation pour patients COVID long.",
      link: "/youtube",
      color: "from-red-600 to-rose-600"
    },
    {
      icon: ClipboardList,
      title: "Évaluer Votre Respiration",
      description: "Tests simples pour évaluer votre mécanique respiratoire, fréquence, score BOLT et score de Nijmegen.",
      link: "/evaluer-respiration",
      color: "from-sky-400 to-sky-500"
    }
  ];

  const benefits = [
    "Réduction des symptômes respiratoires",
    "Amélioration de la tolérance à l'effort",
    "Diminution de l'anxiété",
    "Meilleur sommeil",
    "Équilibre du système nerveux",
    "Augmentation de l'énergie",
    "Réduction de l'inflammation",
    "Amélioration de la concentration"
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFFFF", fontFamily: "Figtree, sans-serif" }} data-testid="home-page">
      <SiteHeader />
      
      {/* Hero Section with background image */}
      <section 
        className="relative py-20 overflow-hidden"
        style={{
          backgroundImage: 'url(/images/homepage-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <div className="text-center max-w-5xl mx-auto">
            {/* Option D: Redesigned Hero - Title first, then logos below */}
            
            {/* Main Title */}
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white drop-shadow-lg mb-8" 
              style={{ 
                fontFamily: 'Outfit, sans-serif',
                textShadow: '2px 2px 8px rgba(0,0,0,0.7), 0 0 20px rgba(0,0,0,0.5)'
              }}
            >
              La rééducation des troubles respiratoires
            </h1>

            {/* Logos side by side - smaller and more balanced */}
            <div className="flex items-center justify-center gap-6 md:gap-10 mb-8">
              {/* Left Logo - Les Mains Nuages */}
              <div className="w-28 h-28 md:w-36 md:h-36 lg:w-44 lg:h-44 flex-shrink-0 rounded-full overflow-hidden bg-white/95 backdrop-blur-sm p-3 flex items-center justify-center shadow-2xl border-2 border-white/50">
                <img 
                  src="/mains-nuages-full-logo.png" 
                  alt="Physiothérapie Les Mains Nuages" 
                  className="w-full h-full object-contain"
                />
              </div>
              
              {/* Decorative divider */}
              <div className="hidden md:flex flex-col items-center gap-2">
                <div className="w-px h-8 bg-gradient-to-b from-transparent via-white/60 to-transparent"></div>
                <div className="w-3 h-3 rounded-full bg-white/40"></div>
                <div className="w-px h-8 bg-gradient-to-b from-transparent via-white/60 to-transparent"></div>
              </div>

              {/* Right Logo - Poumons */}
              <div className="w-28 h-28 md:w-36 md:h-36 lg:w-44 lg:h-44 flex-shrink-0 rounded-full overflow-hidden shadow-2xl border-2 border-white/50">
                <img 
                  src="/enso-right.jpg" 
                  alt="Poumons - Symbole" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <p 
              className="text-lg md:text-xl lg:text-2xl text-white mb-8 leading-relaxed font-medium max-w-4xl mx-auto"
              style={{ 
                textShadow: '1px 1px 6px rgba(0,0,0,0.8), 0 0 15px rgba(0,0,0,0.5)'
              }}
            >
              Vous souffrez d&apos;hyperventilation, de respiration dysfonctionnelle, d&apos;asthme, ou de symptômes liés au COVID Long ? 
              Découvrez des techniques de rééducation respiratoire scientifiquement prouvées pour améliorer votre qualité de vie.
            </p>

            {/* About Me Section */}
            <div className="mb-10 mx-auto max-w-5xl">
              <div className="rounded-2xl p-8 border-2 border-white/30 shadow-lg bg-black/30 backdrop-blur-sm">
                <div className="prose prose-slate max-w-none">
                  <p className="text-base md:text-lg text-white leading-relaxed mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}>
                    Je suis <strong className="text-white">Physiothérapeute depuis 1999</strong>, je me suis passionné pour les troubles respiratoires 
                    depuis 5 ans et ai étudié, pratiqué sur moi-même toutes les techniques respiratoires, et développé une 
                    compréhension dans mon corps du fonctionnement de cette machine organique que j&apos;appelle <em>&quot;Moi&quot;</em> qui me 
                    permet de <strong className="text-white">VOUS comprendre</strong>, d&apos;identifier tous les paramètres de chacune de nos vies, chacune 
                    totalement unique, qui influencent le fonctionnement de cette machine humaine, en perturbent son équilibre, 
                    et la forcent à s&apos;adapter, continuellement, pour un seul but : maintenir la vie, à tout prix.
                  </p>
                  <p className="text-base md:text-lg text-white leading-relaxed" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}>
                    Les symptômes que nous ressentons, les inconforts, les douleurs, reflètent ces adaptations. Ce ne sont pas 
                    des <em>&quot;problèmes&quot;</em>, mais des <strong className="text-white">solutions</strong> que le corps trouve pour s&apos;adapter aux multiples 
                    stresseurs qui tentent de le déséquilibrer. Par la compréhension de ce fonctionnement, et l&apos;analyse de notre 
                    vie propre, on peut retrouver l&apos;équilibre, ramener le corps dans un espace de <em>&quot;sécurité&quot;</em>, et retirer 
                    au corps le besoin de créer ces adaptations. <strong className="text-white">La respiration est une des portes d&apos;entrées</strong> dont 
                    nous disposons pour retrouver cet équilibre, et le bien-être qui en découle.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 justify-center items-center">
              {/* First row of buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/app-landing')}
                  className="px-8 py-6 text-lg rounded-full bg-cyan-500/90 hover:bg-cyan-600 shadow-lg shadow-cyan-200 text-white"
                >
                  Commencer les exercices
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button 
                  onClick={() => navigate('/youtube')}
                  className="px-8 py-6 text-lg rounded-full bg-sky-500/90 hover:bg-red-600 shadow-lg shadow-red-200 text-white"
                >
                  <Youtube className="mr-2" size={20} />
                  Chaîne YouTube
                </Button>
              </div>
              
              {/* Second row of buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/documents?open=theorie')}
                  className="px-8 py-6 text-lg rounded-full0/90 hover:bg-sky-600 shadow-lg shadow-amber-200 text-white"
                >
                  <ClipboardList className="mr-2" size={20} />
                  La Rééducation, document complet
                </Button>
                <Button 
                  onClick={() => navigate('/evaluer-respiration')}
                  className="px-8 py-6 text-lg rounded-full bg-sky-500/90 hover:bg-sky-600 shadow-lg shadow-indigo-200 text-white"
                >
                  <Activity className="mr-2" size={20} />
                  Évaluer votre respiration
                </Button>
              </div>
            </div>
            
            {/* Avertissement Important */}
            <div className="mt-8 mx-auto max-w-5xl">
              <div className="p-6 border-l-4 border-sky-500 rounded-lg shadow-sm bg-white/95">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-slate-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-base text-slate-700 leading-relaxed">
                      <strong className="text-slate-800">Avertissement :</strong> Les informations et les exercices décrits sur ces pages sont à titre informatifs ; les problématiques auxquelles elles s&apos;adressent relèvent d&apos;un diagnostic médical et d&apos;un accompagnement par un professionnel de santé spécialisé. Ces pages sont un résumé très succinct du contenu des séances thérapeutiques que j&apos;effectue selon une progression totalement individualisée par un bilan complet et une connaissance précise de la personne. N&apos;hésitez pas à me contacter pour de plus amples informations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Troubles Respiratoires Traités
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Une approche personnalisée pour chaque condition, basée sur les dernières avancées en physiothérapie respiratoire.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {conditions.map((condition, index) => (
              <Card 
                key={index} 
                className="group cursor-pointer hover:shadow-xl transition-all duration-300 border-0"
                onClick={() => navigate(condition.link)}
              >
                <CardContent className="p-8">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${condition.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <condition.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-medium mb-3">{condition.title}</h3>
                  <p className="text-slate-600 mb-4">{condition.description}</p>
                  <span className="text-sky-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    En savoir plus <ChevronRight size={18} />
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Pourquoi la Rééducation Respiratoire ?
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                La respiration est une fonction vitale que nous effectuons plus de 20 000 fois par jour. 
                Pourtant, de nombreuses personnes développent des patterns respiratoires inadaptés qui peuvent 
                entraîner une multitude de symptômes physiques et psychologiques.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                En tant que physiothérapeute spécialisé, je vous accompagne dans la correction de ces dysfonctionnements 
                à travers des exercices ciblés et des techniques éprouvées, notamment la méthode Buteyko et la cohérence cardiaque.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 p-4 rounded-xl shadow-sm">
                  <CheckCircle className="w-5 h-5 text-sky-500 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Educational Resources Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Comprendre Votre Respiration
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Explorez les ressources éducatives pour mieux comprendre le fonctionnement de votre système respiratoire.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {educationalResources.map((resource, index) => (
              <Card 
                key={index} 
                className="group cursor-pointer hover:shadow-xl transition-all duration-300 border-0"
                onClick={() => navigate(resource.link)}
              >
                <CardContent className="p-8">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${resource.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <resource.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-medium mb-3">{resource.title}</h3>
                  <p className="text-slate-600 mb-4">{resource.description}</p>
                  <span className="text-sky-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    Découvrir <ChevronRight size={18} />
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection />

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Prêt à Améliorer Votre Respiration ?
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Accédez gratuitement à nos exercices de rééducation respiratoire et commencez votre parcours vers une meilleure santé.
          </p>
          <Button 
            onClick={() => navigate('/app-landing')}
            className="px-10 py-6 text-lg rounded-full bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg"
          >
            Commencer maintenant
          </Button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default HomePage;
